import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AnimeCard from '@/components/base/AnimeCard';
import { animeList, animeCategories, siteInfo } from '@/mocks/anime';

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const latestAnime = animeList.filter((a) => a.categorySlug === 'latest').slice(0, 4);
  const popularAnime = animeList.filter((a) => a.categorySlug === 'popular').slice(0, 4);
  const classicAnime = animeList.filter((a) => a.categorySlug === 'classic').slice(0, 4);
  const exclusiveAnime = animeList.filter((a) => a.categorySlug === 'exclusive').slice(0, 4);

  const heroBgImage = 'https://readdy.ai/api/search-image?query=Epic%20cinematic%20anime%20style%20illustration%20of%20a%20vast%20fantasy%20landscape%20with%20floating%20islands%20glowing%20constellations%20and%20aurora%20in%20night%20sky%2C%20dark%20atmospheric%20background%2C%20rich%20crimson%20and%20gold%20accents%2C%20immersive%20cinematic%20composition%2C%20anime%20background%20art%20quality%2C%20deep%20space%20aesthetic&width=1920&height=1080&seq=hero-bg-main&orientation=landscape';

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgImage}
              alt="羞羞动漫 - 高清动漫资源平台"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/50 to-background-950"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/40 via-transparent to-background-950/40"></div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-6 leading-tight">
              探索<span className="text-primary-500">二次元</span>的无限可能
            </h1>
            <p className="text-base md:text-lg text-foreground-300 max-w-[650px] mx-auto mb-8 leading-relaxed">
              {siteInfo.tagline} · 海量高清动漫资源、独家番剧更新与流畅播放体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/latest"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
              >
                立即探索
              </Link>
              <Link
                to="/category/popular"
                className="px-8 py-3 bg-background-800/80 hover:bg-background-700 text-foreground-200 font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap border border-background-700/50"
              >
                热门推荐
              </Link>
            </div>

            <div className="mt-12 flex items-center justify-center gap-8 md:gap-12">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{siteInfo.totalAnime}</p>
                <p className="text-xs text-foreground-500 mt-1">高清番剧</p>
              </div>
              <div className="w-px h-10 bg-background-700"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{siteInfo.totalUsers}</p>
                <p className="text-xs text-foreground-500 mt-1">注册用户</p>
              </div>
              <div className="w-px h-10 bg-background-700"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500">{siteInfo.dailyUpdate}</p>
                <p className="text-xs text-foreground-500 mt-1">每日更新</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-950">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {animeCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 md:p-6 rounded-lg bg-background-900 border border-background-800/50 hover:border-primary-500/30 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 text-xl">
                    <i className={`${
                      cat.slug === 'latest' ? 'ri-flashlight-line' :
                      cat.slug === 'popular' ? 'ri-fire-line' :
                      cat.slug === 'classic' ? 'ri-vip-crown-line' :
                      'ri-star-line'
                    }`}></i>
                  </div>
                  <h3 className="text-foreground-100 font-heading font-semibold text-sm md:text-base mb-1">
                    {cat.name}
                  </h3>
                  <p className="text-foreground-500 text-xs">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-900">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  最新番剧
                </h2>
                <p className="text-foreground-500 text-sm mt-1">每周更新最热番剧，第一时间追番不等待</p>
              </div>
              <Link
                to="/category/latest"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestAnime.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular Recommendations */}
        <section className="py-10 md:py-14 bg-background-950">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  热门推荐
                </h2>
                <p className="text-foreground-500 text-sm mt-1">全网热度最高的精选动漫合集</p>
              </div>
              <Link
                to="/category/popular"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {popularAnime.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </div>
        </section>

        {/* Classic & Exclusive */}
        <section className="py-10 md:py-14 bg-background-900">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                      经典回顾
                    </h2>
                    <p className="text-foreground-500 text-sm mt-1">历久弥新的动漫神作</p>
                  </div>
                  <Link
                    to="/category/classic"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    查看全部 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {classicAnime.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                      独家资源
                    </h2>
                    <p className="text-foreground-500 text-sm mt-1">别处找不到的高品质番剧</p>
                  </div>
                  <Link
                    to="/category/exclusive"
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    查看全部 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {exclusiveAnime.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 bg-background-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-accent-500/5 to-primary-500/5"></div>
          <div className="relative z-10 max-w-[700px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-4">
              准备好开启你的<span className="text-primary-500">二次元之旅</span>了吗？
            </h2>
            <p className="text-foreground-400 text-sm md:text-base mb-8">
              {siteInfo.totalAnime}部高清番剧等你来探索，每日更新，精彩不断
            </p>
            <Link
              to="/category/latest"
              className="inline-block px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap"
            >
              立即免费探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteInfo.name,
            url: 'https://xixiudongman.com',
            description: siteInfo.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://xixiudongman.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
    </>
  );
}